"use client";

import { FORMAT_CREDIT_MAX_DECIMAL } from "@garther/interfaces";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

export function useI18n() {
  const { t, i18n } = useTranslation();

  const changeLanguage = (language: "en" | "vi") => {
    i18n.changeLanguage(language);
  };

  const locale = (i18n.language as "en" | "vi") === "vi" ? "vi-VN" : "en-US";

  const formatDate = useCallback(
    (
      date: Date,
      options: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
      }
    ) => {
      if (!date) return "";
      return date.toLocaleString(locale, options);
    },
    [locale]
  );

  const formatCurrency = useCallback(
    (value: string | number) => {
      if (Number.isNaN(value)) {
        return "Invalid value";
      }
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: "VND"
      }).format(Number(value));
    },
    [locale]
  );

  const formatCredit = useCallback(
    (value: string | number) => {
      return (
        new Intl.NumberFormat(locale, {
          style: "decimal",
          maximumFractionDigits: FORMAT_CREDIT_MAX_DECIMAL
        }).format(Number(value)) + (locale === "vi-VN" ? " xu" : " credit" + (Number(value) > 1 ? "s" : ""))
      );
    },
    [locale]
  );

  return {
    t,
    changeLanguage,
    formatDate,
    formatCurrency,
    formatCredit,
    currentLanguage: i18n.language as "en" | "vi",
    isEnglish: locale === "en-US",
    isVietnamese: locale === "vi-VN"
  };
}
